export const DEFAULT_REVIEW_CURRENCY = 'USD'

export function parseCycleTimestamp(value) {
    if (!value) return 0
    if (typeof value === 'number') {
        return value < 1000000000000 ? value * 1000 : value
    }
    const timestamp = Date.parse(value)
    return Number.isNaN(timestamp) ? 0 : timestamp
}

export function isDeadlinePassed(value) {
    const timestamp = parseCycleTimestamp(value)
    if (!timestamp) return false
    const endOfDay = new Date(timestamp)
    endOfDay.setHours(23, 59, 59, 999)
    return Date.now() > endOfDay.getTime()
}

/** Registration cutoff for a cycle. Empty => not selectable. */
export function getCycleRegisterDeadline(cycle) {
    return cycle && (cycle.register_ddl_date || cycle.register_deadline_date || '')
}

/**
 * Open cycles = have register_ddl_date and that date has not passed.
 * Sorted nearest registration deadline first (= current).
 */
export function getOpenReviewCycles(entryDates, now = Date.now()) {
    const list = Array.isArray(entryDates) ? entryDates : []
    return list
        .filter(cycle => {
            if (!cycle || !cycle.name) return false
            const registerDdl = getCycleRegisterDeadline(cycle)
            if (!registerDdl) return false
            const ts = parseCycleTimestamp(registerDdl)
            if (!ts) return false
            const endOfDay = new Date(ts)
            endOfDay.setHours(23, 59, 59, 999)
            return now <= endOfDay.getTime()
        })
        .slice()
        .sort((a, b) => parseCycleTimestamp(getCycleRegisterDeadline(a)) - parseCycleTimestamp(getCycleRegisterDeadline(b)))
}

export function shouldShowReviewCycle(cycle) {
    if (!cycle || !cycle.name) return false
    const registerDdl = getCycleRegisterDeadline(cycle)
    if (!registerDdl) return false
    return !isDeadlinePassed(registerDdl)
}

export function getCurrentReviewCycle(entryDates, now = Date.now()) {
    const open = getOpenReviewCycles(entryDates, now)
    return open.length ? open[0] : null
}

export function isCurrentReviewCycle(cycle, entryDates, now = Date.now()) {
    const current = getCurrentReviewCycle(entryDates, now)
    if (!current || !cycle) return false
    const selectedName = typeof cycle === 'string' ? cycle : cycle.name
    if (current.name === selectedName) return true
    const selectedDdl = typeof cycle === 'string'
        ? (getOpenReviewCycles(entryDates, now).find(c => c.name === selectedName) || {}).register_ddl_date
        : getCycleRegisterDeadline(cycle)
    return !!selectedDdl && parseCycleTimestamp(selectedDdl) === parseCycleTimestamp(getCycleRegisterDeadline(current))
}

export function getReviewCycleDisplayName(cycle) {
    const name = typeof cycle === 'string' ? cycle : ((cycle && cycle.name) || '')
    return name
}

export function getReviewCurrency(project) {
    return (project && project.project_price_currency) || DEFAULT_REVIEW_CURRENCY
}

export function formatReviewDate(value, formatter) {
    const timestamp = parseCycleTimestamp(value)
    if (!timestamp) return ''
    if (typeof formatter === 'function') {
        return formatter(timestamp)
    }
    return new Date(timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    })
}

export function buildReviewCycleOptionLabel(cycle, formatter) {
    const displayName = getReviewCycleDisplayName(cycle)
    const resultsDate = formatReviewDate(cycle && cycle.announce_date, formatter)
    return resultsDate ? `${displayName} (results: ${resultsDate})` : displayName
}

/** Track fee from project slots: current => normal, non-current/makeup => early bird. */
export function getNimTrackListPrice(project, { isCurrent = false, isMakeup = false } = {}) {
    if (!project) return 0
    if (isMakeup || !isCurrent) {
        const early = project.project_earlybird_price
        if (early !== undefined && early !== null && early !== '') return Number(early)
    }
    return Number(project.project_price || 0)
}

export function getNimNilListPrice(project) {
    return Number((project && project.project_price) || 0)
}

export function buildReviewCycleFeeLines(cycle, project, formatter, options = {}) {
    if (!cycle || !project) return []
    const currency = getReviewCurrency(project)
    const registerBy = formatReviewDate(getCycleRegisterDeadline(cycle), formatter) || 'the registration deadline'
    const isNil = /NIL$/i.test(String(project.project_code || ''))
    const isMakeup = !!options.isMakeup
    const isCurrent = isMakeup ? false : isCurrentReviewCycle(cycle, options.entryDates || (project.projectManage && project.projectManage.entry_date) || options.allCycles || [], options.now)

    if (isNil) {
        return [{
            key: 'nil',
            text: `${getNimNilListPrice(project)} ${currency} register by ${registerBy}`
        }]
    }

    const price = getNimTrackListPrice(project, { isCurrent, isMakeup })
    const label = isMakeup ? 'Track (with NIL)' : (isCurrent ? 'Accelerated (current window)' : 'Standard (later window)')
    return [{
        key: isMakeup ? 'makeup' : (isCurrent ? 'current' : 'non_current'),
        text: `${label}: ${price} ${currency} register by ${registerBy}`
    }]
}

/** REQ-002 / REQ-004: combo copy uses combined NIL + track list price. */
export function buildComboCycleCopyLines(cycle, trackProject, nilProject, formatter, options = {}) {
    if (!cycle || !trackProject) return []
    const currency = getReviewCurrency(trackProject)
    const registerBy = formatReviewDate(getCycleRegisterDeadline(cycle), formatter) || 'the registration deadline'
    const isCurrent = isCurrentReviewCycle(cycle, options.entryDates || [], options.now)
    const trackPrice = getNimTrackListPrice(trackProject, { isCurrent, isMakeup: false })
    const nilPrice = getNimNilListPrice(nilProject || {})
    const total = Number(nilPrice || 0) + Number(trackPrice || 0)
    const selectedResults = formatReviewDate(cycle.announce_date || cycle.results_date, formatter) || 'results date'
    const currentCycle = getCurrentReviewCycle(options.entryDates || [], options.now)
    const currentResults = formatReviewDate(
        currentCycle && (currentCycle.announce_date || currentCycle.results_date),
        formatter
    ) || 'results date'
    const lines = [{
        key: 'combo_price',
        text: `Combo price: ${total} ${currency}, register by ${registerBy}`
    }]
    if (isCurrent) {
        lines.push({
            key: 'accelerated',
            text: `Accelerated: NIL and the selected track run concurrently (results: ${selectedResults})`
        })
    } else {
        lines.push({
            key: 'standard',
            text: `Standard: Complete NIL first (results: ${currentResults}), then the selected track (results: ${selectedResults})`
        })
    }
    return lines
}

// ---- legacy shims (unused by new NIM flow; kept for soft imports) ----
export function isAcceleratedReview() {
    return false
}
export function getEarlyBirdDeadline(project) {
    return project && (
        project.earlybird_deadline_date ||
        project.project_earlybird_deadline_date ||
        project.early_bird_deadline_date ||
        project.earlyBirdDeadline
    )
}
export function getStandardDeadline(project) {
    return project && (
        project.deadline_date ||
        project.project_deadline_date ||
        project.registration_deadline_date ||
        project.standard_deadline_date
    )
}
export const ACCELERATED_REVIEW_PRICE = 0
export const STANDARD_REVIEW_PRICE = 0
export const EARLY_BIRD_REVIEW_PRICE = 0
